/**
 * Help section copy (Japanese). Same keys as helpSections.en.ts
 */
import type { HelpSectionMap } from './helpSections.en';

const helpSectionsJa: HelpSectionMap = {
  'getting-started': {
    intro:
      '始めるために必要なことがまとまっています。手順に従うか、サイドバーから探してください。',
    stepsTitle: '最初のステップ',
    step1: '**概要** を開いてダッシュボードと最近のアクティビティを確認します。',
    step2: '**マイタスク** を開いて自分に割り当てられた課題を確認します。',
    step3: 'サイドバーの **プロジェクト** をクリックしてボード、バックログ、ロードマップなどを開きます。',
    stepPm:
      '**プロジェクト管理** を開くと全プロジェクトとロードマップ概要（現在の段階、次のデプロイ、計画日）が表示されます。',
    step4: 'ヘッダーの **作成** から新しい課題を追加します。',
    step5:
      'ヘッダーの **テーマ切り替え**（太陽/月アイコン）でライト/ダークモードを切り替えます。',
    outro:
      '左のリンクで各トピックへ移動するか、上のクイックツアーで短い案内を受けられます。',
  },
  notifications: {
    intro:
      'ヘッダーのベルはアプリ内通知です。クリックして通知センターを開き、既読にできます。',
    whatTitle: '通知の内容',
    n1: '誰かが課題をあなたに割り当てたとき',
    n2: 'コメントで **@メンション** されたとき',
    n3: 'ステータス変更、コメント、期限リマインダー',
    n4: 'プロジェクト招待（組織で利用している場合）',
    prefsTitle: '設定の管理',
    prefsBody:
      'プロフィールまたはアカウント設定で、メールとアプリの通知の種類（例：割り当てのみ、メンションも）を選べます。',
    sessionsBody:
      'プロフィールから **アクティブなセッション** を表示し、他のデバイスからサインアウトできます。',
    tip: 'ベルを開き「すべて既読にする」をクリックして一覧を消去します。',
  },
  'keyboard-shortcuts': {
    intro:
      'メインコンテンツにフォーカスがあるときに使えます。Mac は **Cmd**、Windows/Linux は **Ctrl** です。',
    k1: '**Cmd/Ctrl + K** または **/** – 課題とプロジェクトを検索（スポットライト）。課題モーダル表示中は無効。',
    k2: '**Cmd/Ctrl + I** – 課題作成を開く',
    k3: '**Cmd/Ctrl + Enter** – 課題作成フォームを送信',
    k4: '**Cmd/Ctrl + S** – 保存（ドキュメントエディター）',
    k5: '**Escape** – モーダルまたは検索を閉じる',
    tip: '/ または Cmd/Ctrl + K でページを離れずに検索できます。リスト表示は大量リスト向けに仮想スクロールします。',
  },
  projects: {
    intro:
      'プロジェクトには課題、スプリント、レポートがあります。サイドバーから参加中のプロジェクトを開けます。',
    createTitle: 'プロジェクトの作成',
    createBody:
      '**プロジェクト管理** で **プロジェクトを作成** をクリック。名前、説明、カテゴリを追加します。テンプレートでワークフローと設定を事前入力できます。',
    membersTitle: 'メンバー管理',
    membersLead:
      '**プロジェクト管理** で全プロジェクト、CSV エクスポート、**ロードマップ概要**（各プロジェクトの段階、次のデプロイ、計画日）を確認します。プロジェクトを開き **メンバーを管理** で：',
    m1: 'ユーザーを追加し、役割（管理者、メンバー、閲覧者）を設定します。',
    m2:
      '**グループ**（管理設定 → グループ）を追加すると、グループ全員に同じ役割が付きます。',
    m3: '役割を変更したりメンバーを外したりします。',
    rolesHint:
      '閲覧者 = 閲覧のみ。メンバー = 課題の編集とコメント。管理者 = メンバーと設定の管理。プロジェクト管理者は少なくとも1人必要です。',
    notePlan:
      'プロジェクト作成は組織のプラン（Free、Pro、Enterprise）で制限される場合があります。下の「プランと制限」を参照。',
    memberOnly:
      'プロジェクト作成は管理者とプロジェクトマネージャーのみです。必要なら管理者に依頼するか、アクセス申請フローを使ってください。',
  },
  issues: {
    intro:
      '課題は追跡する作業項目です：タスク、バグ、ストーリー、エピック、サブタスク。',
    createTitle: '課題の作成',
    c1: 'ヘッダーの **作成** をクリックします。',
    c2: '**課題を作成** または **CSV から一括作成** を選びます。',
    c3: 'フォーム（プロジェクト、タイトル、種類、期限など）を入力して送信します。',
    createTip: 'プロジェクトのボードやバックログからも課題を作成できます。',
    typesTitle: '課題の種類',
    typeTask: '**タスク** – 一般的な作業',
    typeStory: '**ストーリー** – ユーザー向け機能',
    typeBug: '**バグ** – 修正が必要なもの',
    typeEpic: '**エピック** – 大きな取り組み。他の課題がリンク可能',
    typeSub: '**サブタスク** – 別の課題の子',
    fieldsTitle: '設定できる項目',
    fieldsBody:
      'タイトル、説明、担当者、優先度、ステータス、期限が基本です。ラベル、コンポーネント、バージョン、親またはエピックのリンク、見積もり、添付も追加できます。作成時に **リンクされた課題** を追加し、リンク種類（例：ブロック、ブロックされる、関連、重複）を選べます。次のリンクのデフォルトとして記憶されます。課題を開いてすべて編集し、コメントを追加します。',
  },
  backlog: {
    intro:
      'スプリントに入っていない課題を一覧し、スプリントを管理します。ドラッグでバックログの並べ替えやスプリントへの移動ができます。',
    b1: '作業を計画するために課題をスプリントにドラッグします。',
    b2: 'スプリントを作成し、計画中 → アクティブ → 完了へ移動します。',
    b3: '必要なら課題をバックログに戻します。',
    b4:
      '大きなバックログはパフォーマンスのため仮想スクロールを使います。ドラッグ＆ドロップの並び順は従来どおりです。',
  },
  'project-management': {
    intro:
      '管理者とプロジェクトマネージャー向け。サイドバーの **プロジェクト管理** で全プロジェクトと高レベルのロードマップ概要を表示します。',
    allTitle: 'すべてのプロジェクト',
    allBody:
      '表に組織の全プロジェクトが表示されます。新規作成、CSV エクスポート、各プロジェクトのボード・バックログ・ロードマップ・ドキュメントを開けます。プロジェクトごとに **メンバーを管理** でユーザーを追加し役割を設定します。',
    archivedBody:
      '**アーカイブを表示** をオンにするとアーカイブ済みプロジェクトも含みます。組織管理者とプロジェクト管理者は **操作** からアーカイブまたは復元できます。アーカイブは既定の一覧とほとんどの選択肢からは非表示です。',
    roadmapTitle: 'ロードマップ概要',
    roadmapLead:
      'ページ上部の **ロードマップ概要** には各プロジェクトについて次が表示されます：',
    ro1: '**現在の段階** – 最新のリリース済みバージョン（ある場合）。',
    ro2: '**次のデプロイ** – 次に計画されているバージョン名。',
    ro3: '**計画日** – 設定されている場合のそのバージョンのリリース日。',
    roadmapFoot:
      '各プロジェクトの位置と次のリリース時期を把握できます。プロジェクト名または **ロードマップ全体を表示** でクロスプロジェクトまたはプロジェクトのロードマップを開きます。',
    roadmapNote:
      'バージョンとリリース日はプロジェクトごとに設定します（管理設定またはプロジェクト設定）。バージョンを追加し、課題に **修正バージョン** を設定するとロードマップが埋まります。',
  },
  roadmap: {
    intro:
      'ロードマップはバージョンとリリースを示し、計画と時期が分かります。FlowBoard にはプロジェクト単位と組織全体のクロスプロジェクトロードマップがあります。',
    projTitle: 'プロジェクトのロードマップ',
    projLead: 'プロジェクトを開き、サイドバーで **ロードマップ** を選ぶと次が表示されます：',
    pr1:
      '**現在の段階**（最新リリース）、**次のデプロイ**（次の計画バージョン）、**計画日**（リリース日）のサマリーカード。',
    pr2:
      '**リスト表示** – 説明、開始/リリース日、課題（修正バージョン / 影響バージョン）を含むバージョンのタイムライン。',
    pr3:
      '**タイムライン表示** – バージョンをバーで表示。課題バッジをドラッグして課題の **修正バージョン** を変更。',
    fixBody:
      'プロジェクト（または組織）にバージョンを追加し、課題に **修正バージョン** / 影響バージョンを設定してロードマップに表示します。',
    crossTitle: 'クロスプロジェクトロードマップ',
    crossBody:
      'サイドバーから **クロスプロジェクトロードマップ** を開くと、全プロジェクトのバージョンが一覧になります。各行にプロジェクト、バージョン名、リリース日、課題数（修正 / 影響）が表示されます。プロジェクト名をクリックしてそのロードマップを開きます。',
    tip: 'ロードマップで関係者と現状と次のデプロイ計画を揃えます。バージョンに開始日とリリース日を設定するとタイムラインが正確になります。',
  },
  calendar: {
    body: '期限のある課題はカレンダーに表示されます。月を切り替えて計画や期限超過を確認します。',
  },
  'list-view': {
    intro:
      'プロジェクト内の全課題の表。並べ替え、フィルター、ステータスや担当者の一括編集ができます。',
    lv1: '高度なフィルターでステータス、担当者、ラベルなどで絞り込みます。',
    lv2: '複数の課題を選び、ステータス、担当者の変更や一括削除ができます。',
    lvBulk:
      '一括編集で多数の課題を一度に更新します。プロジェクトのリストからはラベル、コンポーネント、修正バージョン、解決結果も設定できます。',
    lv3:
      'リストが大きいときは下部の **ページング** でページ移動と1ページあたりの件数を調整します。',
    lv4:
      '**保存したレイアウト** はこのブラウザにフィルター、検索、並べ替え、列の選択を保存します（サーバー側の保存フィルターとは別）。**SLA** 列を表示すると SLA 違反中の課題が分かります。',
    tip: '多くの課題を比較したり一括更新するのにリスト表示が適しています。',
  },
  'dependency-graph': {
    body: '課題のリンク（ブロック、ブロックされる、関連）を視覚化します。影響とクリティカルパスの把握に使います。',
  },
  docs: {
    intro:
      '各プロジェクトに仕様書や運用手順、議事録向けの **ドキュメント** 領域（Confluence 風）があります。プロジェクトを開きサイドバーの **ドキュメント** へ。',
    tplTitle: 'ページテンプレートとラベル',
    tplBody:
      'ページの作成・編集時に **テンプレート**（例：議事録、仕様）を選び、**ラベル**（例：spec、meeting-notes）で整理・検索しやすくできます。',
    sidebarTitle: '左サイドバー',
    sidebarBody:
      '左に全ページのツリーが表示されます。**新しいページ** で作成し、検索ボックスでタイトル絞り込み。フォルダは矢印で開閉、タイトルクリックで開きます。',
    pageTitle: 'ページ上',
    d1: '**このページで** – 表示時（編集時は除く）に H2/H3 見出しから固定の目次。',
    d2: '**子ページ** – サブページがある場合、コンテンツ下にリンク付きで一覧。',
    d3: '**編集** – タイトル、親、本文を変更。ツールバーで書式、リンク、画像。',
    d4: '**履歴** – 以前のバージョンを表示・復元。',
    d5: '**エクスポート** – Markdown としてダウンロード。',
    d6: 'コメントは下部。返信の追加と閲覧。',
    tip: 'ドキュメントエディターで **Ctrl + S** を押すとページを離れずに保存できます。',
  },
  reports: {
    intro:
      'プロジェクトから **レポート** を開くと、ベロシティ、累積フロー、管理図、エピック進捗、時間追跡、レポートビルダー、**ワークフロー**（ガント）表示があります。共有時にデータやワークフロー図をエクスポートできます。',
    typesTitle: 'レポートの種類',
    r1: '**ベロシティ** – スプリントあたりの完了作業。',
    r2: '**累積フロー** – 時間経過に伴うステータス間の課題の動き。',
    r3: '**管理図** – 完了課題のサイクルタイム。',
    r4:
      '**エピックレポート** – **エピック** ドロップダウンで1つのエピックまたはすべてを表示。エクスポートボタンからデータを出力。',
    r5: '**時間追跡** – 種類とユーザー別の見積もりと実績。',
    r6:
      '**ワークフロー（ガント）** – 期限付き課題のタイムライン：階層（エピック→タスク→サブタスク）、進捗バー、依存線（親子と「ブロック」などのリンク）、マイルストン（同日をひし形）。ズーム（日/週/月）と **画像としてエクスポート** で PNG 保存。バーの色はプロジェクトのステータス色。',
    r7: '**レポートビルダー** – ステータス、種類、優先度、担当者でグループ化。',
    tip: 'ベロシティと累積フローでチームのスループットとボトルネックを把握します。',
  },
  dashboards: {
    body: '**カスタムダッシュボード** でウィジェット（プロジェクト、グループ、担当者別）を組み合わせて自分のダッシュボードを作れます。グループや役割で表示権限を制御できます。',
  },
  'admin-settings': {
    intro:
      'サイドバーの **管理設定** で組織とプロジェクトのセットアップを管理します。',
    orgTitle: '組織',
    orgBody: '組織名の編集、メンバー管理、保留中の招待の処理。',
    projCfgTitle: 'プロジェクト設定',
    projCfgBody:
      'プロジェクト全体で使う **ラベル**、**コンポーネント**、**バージョン**、**カスタムフィールド** を管理します。',
    tplTitle: 'テンプレート',
    tplBody:
      '**プロジェクト** と **課題** のテンプレートが新規プロジェクト・課題の既定のワークフローとフィールドを設定します。',
    autoTitle: '自動化',
    autoBody:
      '課題が条件に一致したときに動くルール（例：ステータス変更、担当者設定）を作成します。トリガーとアクションで繰り返し作業を自動化。表の **編集**（鉛筆）で名前、説明、トリガー、有効状態を変更、**削除** でルールを削除します。',
    groupsTitle: 'グループ',
    groupsBody:
      'グループを作成しメンバーを追加し、プロジェクトに割り当て（メンバー管理中）すると、グループ全員が同じプロジェクト役割になります。',
    rolesTitle: '役割と監査ログ',
    rolesBody: 'カスタム役割の管理と重要な変更の監査ログの表示。',
    systemTitle: 'システム（スーパー管理者のみ）',
    systemBody:
      '**システム** タブに全組織が表示されます。各組織の **プラン**（Free、Pro、Enterprise）の設定や新規企業の招待ができます。',
  },
  glossary: {
    intro: 'FlowBoard で使う用語の短い定義です。',
    g1: '**課題** – 作業項目：タスク、ストーリー、バグ、エピック、サブタスク。',
    g2: '**エピック** – 大きな取り組み。他の課題がリンク可能。',
    g3: '**スプリント** – 期間で区切った課題の集合（例：2週間）。',
    g4: '**バックログ** – まだスプリントに入っていない課題。',
    g5: '**バージョン** – リリースやマイルストーン（例：v1.0）。開始・リリース日は任意。リリース済みに設定可能。',
    g6: '**修正バージョン** – 課題の修正・提供が計画されているバージョン。ロードマップに表示。',
    g7: '**影響バージョン** – 課題が影響するバージョン（バグなど）。ロードマップにも表示。',
    g8: '**Kanban ボード** – ステータス別の列。カードをドラッグして更新。',
    g9: '**ワークフロー** – ステータスと遷移（例：未着手 → 進行中 → 完了）。',
    g10: '**担当者** – 課題の責任者。',
    g11: '**ウォッチャー** – 更新通知を受け取るユーザー。',
  },
  'subscription-plans': {
    body: 'Free プランはプロジェクト数とユーザー数に制限があります。上位プランではより多く（無制限を含む）利用できます。上限に達するとプラン更新まで作成がブロックされます。スーパー管理者は管理設定 → システムで各組織のプランを設定します。',
  },
  'api-integrations': {
    intro:
      'FlowBoard は課題、プロジェクト、レポート、webhook など向けの REST API を公開しています。インスタンスの API ベース URL と有効な認証トークン（ログイン後など）でリクエストします。',
    webhooksTitle: 'Webhooks',
    webhooksBody:
      '組織管理者は **管理 → Webhooks** で送信 webhook を設定します。課題・プロジェクトのイベントで HTTP POST を送り、検証用に HMAC シークレットを任意で設定できます。Slack へは Slack の Incoming Webhook URL で webhook を追加するか、自動化ルールの Webhook アクションを使います。',
    openapiNote:
      'バックエンド起動中は **/api/docs/openapi.json** で OpenAPI 3.0 仕様を取得できます。Swagger UI などで利用してください。',
  },
};

export default helpSectionsJa;
